import { DeployMicroserviceRequest, MicroserviceConfigurationItem } from "@microtica/ms-kube-sdk";
import { notification } from "antd";
import { NavigateFunction } from "react-router";
import { getKubeService } from "../../backend";
import { IAppMonitoring } from "../../types";
import { AXIOS_TIMEOUT_ERROR_CODE } from "./assign-domain-steps";

interface IRestartAppProps {
    projectId: string;
    envId: string;
    app: {
        name: string;
        namespace: string;
        clusterId: string;
        image: string;
        configurations?: MicroserviceConfigurationItem[];
        monitoring?: IAppMonitoring;
    },
    navigate?: NavigateFunction;
    options?: { timeout: number };
}

interface IDeleteAppProps extends Omit<IRestartAppProps, "app" | "navigate"> {
    app: {
        name: string;
        namespace: string;
        clusterId: string;
    },
    navigate: NavigateFunction;
}

// DELETE APP
export const handleDeleteApp = async ({
    envId,
    projectId,
    app,
    navigate
}: IDeleteAppProps) => {
    try {
        const { data: response } = await getKubeService().undeployMicroservice(
            app.name,
            app.namespace,
            app.clusterId,
            projectId);

        if (response.done) {
            navigate(`/projects/${projectId}/environments/${envId}/apps`);

            notification.success({
                message: `Application deleted`,
                description: `Application ${app.name} was successfully deleted`,
            });
        }
    } catch (error) {
        notification.error({
            message: "Error deleting application",
            description: (error as any).response.data.message
        });
    }
}


// RESTART APP
export const deployApplication = async ({
    projectId,
    app,
    options
}: Omit<IRestartAppProps, "navigate" | "envId">) => {
    const { image, configurations, monitoring, clusterId, namespace, name } = app;
    const microserviceRequest: DeployMicroserviceRequest = {
        deployment: {
            image,
            configurations,
            containerPort: parseInt(configurations?.find(c => c.key === "MIC_CONTAINER_PORT")?.value || "80")
        },
        autoScaling: {
            cpu: monitoring?.initialCpu,
            maxCpu: monitoring?.cpuLimit,
            memory: monitoring?.initialMemory,
            maxMemory: monitoring?.memoryLimit,
            cpuUtilization: 80,
            memoryUtilization: 80,
            minReplicas: monitoring?.minReplicas,
            maxReplicas: monitoring?.maxReplicas
        }
    }

    await getKubeService().deployMicroservice(
        name,
        clusterId,
        namespace,
        projectId,
        microserviceRequest,
        options
    );
}

export const handleRestartApp = async ({
    projectId,
    envId,
    app,
    navigate,
    options
}: IRestartAppProps) => {
    try {
        await deployApplication({
            projectId,
            app,
            options
        });

        notification.success({
            message: "Restart initiated",
            description: "It would take a few moments to apply the changes."
        });
        navigate?.(`/projects/${projectId}/environments/${envId}/apps/${app.clusterId}/${app.namespace}/${app.name}/deployments`);
    } catch (error: any) {
        if (!!options?.timeout && error?.code === AXIOS_TIMEOUT_ERROR_CODE) {
            // 504 gateway timeout occurred, skip this error 
            notification.success({
                message: "Restart initiated",
                description: "It would take a few moments to apply the changes."
            });
        } else {
            notification.error({
                message: "Restart failed",
                description: error.response.data.message
            });
        }
    }
}